import React, { useCallback, useMemo, useState } from 'react';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { useHistory, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Preloader from '@/common/components/Preloader';
import { useLocalizedField } from '@/common/hooks';
import { useCurrentDecedentId } from '@/app/providers/DecedentProvider';
import { routes } from '@/app/routing';
import { useFinancialInstitutionsQuery } from '@/entities/question';
import { useGeneralMetaDataQuery } from '@/entities/meta-data';
import {
  FinancialDetailsSection,
  FinancialInstitutionDeleteButton,
} from '@/modules/financial-details/components';
import { FinancialSectionDataEntryModal } from '@/modules/financials/components';
import { SECTION_CONFIG } from '@/modules/financial-details/config';
import styles from '@/modules/financial-details/financial-details.styles';
import { Stack } from '@mui/material';

const FinancialDetailsModule = () => {
  const history = useHistory();
  const { institutionId } = useParams();
  const { t } = useTranslation();
  const getLocalizedField = useLocalizedField();
  const decedentId = useCurrentDecedentId();
  const [activeModal, setActiveModal] = useState(null);
  const { data: metaData = {} } = useGeneralMetaDataQuery();
  const { dropdowns = {} } = metaData;
  const {
    data: institutions = [],
    isLoading,
    refetch,
  } = useFinancialInstitutionsQuery(decedentId);

  const institution = useMemo(
    () => institutions.find(({ id }) => String(id) === String(institutionId)),
    [institutionId, institutions]
  );

  const closeModal = useCallback(() => {
    setActiveModal(null);
  }, []);

  const openModal = useCallback((sectionKey) => {
    setActiveModal(sectionKey);
  }, []);

  const handleBack = useCallback(() => {
    history.push(routes.dashboard.finInstitutions);
  }, [history]);

  const activeModalTitle = useMemo(() => {
    if (!activeModal || !institution?.name) {
      return undefined;
    }

    return t(SECTION_CONFIG[activeModal].modalTitleKey, {
      finInstName: institution.name,
    });
  }, [activeModal, institution?.name, t]);

  const getDisplayValue = useCallback(
    (value, key, props) => {
      if (!value || !props?.properties?.length) {
        return value;
      }

      const property = props.properties.find((item) => item.key === key);

      if (!property?.type) {
        return value;
      }

      const options =
        getLocalizedField(dropdowns, property.type) ||
        dropdowns?.[property.type] ||
        [];

      if (!options.length) {
        return value;
      }

      const matched =
        options.find((option) => option.value === value) ||
        options.find((option) => option.label === value);

      return matched?.label || value;
    },
    [dropdowns, getLocalizedField]
  );

  if (isLoading) {
    return <Preloader />;
  }

  if (!institution) {
    return (
      <>
        <Box sx={styles.pageHeader}>
          <Button
            color="success"
            size="medium"
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
          >
            {t('Back')}
          </Button>
        </Box>
        <Box sx={{ ...styles.body, height: 'calc(100% - 92px)' }}>
          <Typography sx={{ m: 'auto' }}>
            {t('Financial institution not found.')}
          </Typography>
        </Box>
      </>
    );
  }

  return (
    <>
      <Box sx={styles.pageHeader}>
        <Button
          color="success"
          size="medium"
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{ alignSelf: 'flex-start' }}
        >
          {t('Back')}
        </Button>
      </Box>
      <Box sx={styles.body}>
        <Typography variant="h4">{institution.name}</Typography>
        <Stack gap={4} mt={2} overflow="auto" maxHeight="calc(100% - 120px)">
          {Object.entries(SECTION_CONFIG).map(([key, config]) => {
            const sectionData = institution[key];

            return (
              <FinancialDetailsSection
                key={key}
                title={t(config.titleKey)}
                entries={sectionData?.list || []}
                fields={config.fields}
                emptyText={t(config.emptyTextKey)}
                actionText={t(config.actionTextKey)}
                onAction={() => openModal(key)}
                getDisplayValue={(value, valueKey) =>
                  getDisplayValue(value, valueKey, sectionData?.props)
                }
                t={t}
              />
            );
          })}
        </Stack>
        <FinancialInstitutionDeleteButton
          institution={institution}
          isLastInstitution={institutions.length === 1}
          refreshTask={refetch}
        />
      </Box>
      <FinancialSectionDataEntryModal
        data={activeModal ? institution?.[activeModal] : null}
        isAddMode={
          activeModal ? !(institution?.[activeModal]?.list || []).length : false
        }
        onClose={closeModal}
        refreshTask={refetch}
        title={activeModalTitle}
      />
    </>
  );
};

export default FinancialDetailsModule;
